import { useEffect } from 'react'
import { NextPage, NextPageContext } from 'next'
import {
  getImagesFromVariantOrPlaceholder,
  getVariantFromProduct,
  IProduct,
  IVariant,
} from '@core/api/Products/types'
import CustomPrismicPage from '@core/components/CustomPrismicPage'
import Error from '@core/components/Error'
import LYSBreadcrumbJsonLd from '@core/components/Primitives/LYSBreadcrumbJsonLd'
import LYSProductJsonLd from '@core/components/Primitives/LYSProductJsonLd'
import ProductDetail from '@core/components/ProductDetail'
import HeadTags from '@core/components/shared/HeadTags'
import routes from '@core/config/routes'
import { viewProductEvent } from '@core/events/view'
import { useTranslation } from '@core/i18n/i18n'
import { IPageLayout, IProductContent } from '@core/prismic/types'
import { compose } from '@core/utils/compose'
import { useServices } from '@core/utils/ioc'
import { PartialDefaultPageProps } from '@core/utils/pages/getDefaultPageProps'
import { PageType } from '@core/utils/pages/types'
import { withPageType } from '@core/utils/pages/withPageType'
import { ensureContentUrl, notFound } from '@core/utils/routing/router'
import { getProductPageMetaData } from '@core/utils/seo/getProductPageMetaData'
import { formatPageTitle } from '@core/utils/title'
import withErrorBoundary from '@core/utils/withErrorBoundary'

export interface IProductPageProps extends PartialDefaultPageProps {
  product?: IProduct
  variant?: IVariant
  prismicContent?: IProductContent
  pageLayout?: IPageLayout
}

export const ProductDetailPage: NextPage<IProductPageProps> = ({
  product,
  variant,
  prismicContent,
  pageLayout,
}) => {
  const { t } = useTranslation()
  const { eventBus } = useServices()

  useEffect(() => {
    if (product && variant) eventBus.publish(viewProductEvent(product))
  }, [product, variant, eventBus])

  if (!product || !variant) return <Error statusCode={404} />

  const { name: productName } = product

  const mainImage = getImagesFromVariantOrPlaceholder(variant)[0].url

  const { seoTitle, seoDescription, keywords } = getProductPageMetaData({
    product,
    prismicContent,
    titleFallback: t('seo.defaultTitles.product', { productName }),
    descriptionFallback: t('seo.defaultDescriptions.product', {
      productName,
    }),
  })

  return (
    <>
      <HeadTags
        title={formatPageTitle(seoTitle)}
        description={seoDescription}
        ogImage={mainImage}
        ogType="product"
        keywords={keywords.join(',')}
      />
      {pageLayout ? (
        <CustomPrismicPage pageLayout={pageLayout} />
      ) : (
        <ProductDetail product={product} variantId={variant.id} />
      )}
      <LYSProductJsonLd product={product} variant={variant} />
      <LYSBreadcrumbJsonLd breadcrumbs={product.breadcrumbs.hierarchy} />
    </>
  )
}

ProductDetailPage.getInitialProps = async (
  ctx: NextPageContext
): Promise<IProductPageProps> => {
  const { services, res, query } = ctx
  const { api, prismicApi } = services
  const [slug, id] = query.slugSlashId

  if (!slug || !id) {
    return notFound(res)
  }
  const [prismicContent, product] = await Promise.all([
    slug ? prismicApi.getProduct(slug).catch((_e) => undefined) : undefined,
    api.products.fetchProductDetailByVariant(id!),
  ])

  const pageLayout = prismicContent?.data.custom_layout.id
    ? await prismicApi.getPageLayoutById(prismicContent?.data.custom_layout.id)
    : undefined

  const variant = product && getVariantFromProduct(product, id)

  if (!product || !variant) {
    return notFound(res)
  }

  ensureContentUrl(ctx, routes.productDetail(variant.slug, variant.id))

  return {
    product,
    variant,
    prismicContent,
    pageLayout,
  }
}

export default compose<typeof ProductDetailPage>(
  withErrorBoundary,
  withPageType(PageType.product)
)(ProductDetailPage)
